import {
    Autocomplete,
    Box,
    Checkbox,
    FilterOptionsState,
    FormControlLabel,
    TextField,
    Typography,
} from '@mui/material';
import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';

import { TrackedButton as Button } from '../../../../../../components/TrackedComponents';
import { useAlert } from '../../../../../../hooks/alert';
import useApi from '../../../../../../hooks/api';
import useDevices from '../../../../../../hooks/devices';
import { normalizeLicensePlate } from '../../../../../../utils/Str';

interface ChangeVINSelectionProps {
    installerName: string;
    onBack: () => void;
    onSuccess: (deviceId: string, isNewInstallation: boolean) => void;
}

const ChangeVINSelection: React.FC<ChangeVINSelectionProps> = (props) => {
    const [selectedVIN, setSelectedVIN] = useState<string>('');
    const [newVIN, setNewVIN] = useState<string>('');
    const [newVINError, setNewVINError] = useState<string>('');
    const [isNewInstallation, setIsNewInstallation] = useState<boolean>(false);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const { t } = useTranslation();
    const { devices } = useDevices();
    const { installApi } = useApi();
    const history = useHistory();
    const [alertElement, setAlert] = useAlert();

    // Get unique license plates (VINs) from devices
    const uniqueVINs = Array.from(
        new Set(devices.filter((d) => d.device.license_plate).map((d) => d.device.license_plate)),
    ).sort((a, b) => normalizeLicensePlate(a).localeCompare(normalizeLicensePlate(b)));

    const isDuplicateVIN = useCallback(
        (vin: string) => {
            const normalized = normalizeLicensePlate(vin);
            if (!normalized) {
                return false;
            }
            return uniqueVINs.some((existing) => normalizeLicensePlate(existing) === normalized);
        },
        [devices],
    );

    const handleDone = useCallback(() => {
        if (!selectedVIN || !newVIN) {
            setAlert({ message: t('setup_tool.change_vin.error_no_vin'), type: 'error', duration: 6000 });
            return;
        }

        if (isDuplicateVIN(newVIN)) {
            setAlert({ message: t('setup_tool.change_vin.error_vin_exists'), type: 'error', duration: 6000 });
            setNewVINError(t('setup_tool.change_vin.error_vin_exists'));
            return;
        }

        setIsLoading(true);

        // Make API call to change VIN using the generated API client
        // Find the device ID from the selected VIN
        const device = devices.find((d) => d.device.license_plate === selectedVIN);
        const deviceId = device?.device.device_id || '';

        installApi
            .installChangeVinPost({
                changeVINRequest: {
                    old_vin: selectedVIN,
                    new_vin: newVIN,
                    installer: props.installerName,
                    is_new_installation: isNewInstallation,
                },
            })
            .then(() => {
                setAlert({ message: t('setup_tool.change_vin.success'), type: 'success', duration: 6000 });
                if (isNewInstallation && deviceId) {
                    // For new installation, go to verification page
                    // The new device will have the same device_id, so we can use it
                    props.onSuccess(deviceId, true);
                } else {
                    // For simple VIN change, go back to tasks
                    history.push('/tasks');
                }
            })
            .catch(() => {
                setAlert({ message: t('setup_tool.change_vin.error'), type: 'error', duration: 6000 });
            })
            .finally(() => {
                setIsLoading(false);
            });
    }, [selectedVIN, newVIN, isNewInstallation, props.installerName, isDuplicateVIN]);

    const isActionDisabled = !(selectedVIN && newVIN);

    return (
        <>
            {alertElement}
            <Box
                sx={{
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                }}
            >
                <Typography variant="h6" textAlign="center">
                    {t('setup_tool.change_vin.header')}
                </Typography>
                <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Autocomplete
                        fullWidth
                        id="vin-select-field"
                        options={uniqueVINs}
                        value={selectedVIN || null}
                        onChange={(_e, value) => {
                            setSelectedVIN(value ?? '');
                        }}
                        renderInput={(params) => (
                            <TextField {...params} fullWidth label={t('setup_tool.change_vin.select_existing_vin')} />
                        )}
                        filterOptions={(options: string[], params: FilterOptionsState<string>) => {
                            const filtered = options.filter((option) =>
                                normalizeLicensePlate(option).includes(normalizeLicensePlate(params.inputValue)),
                            );
                            return filtered;
                        }}
                        getOptionLabel={(option) => option}
                        sx={{ mt: 1 }}
                        clearOnEscape
                        clearOnBlur={false}
                    />
                    <TextField
                        fullWidth
                        id="new-vin-field"
                        label={t('setup_tool.change_vin.new_vin')}
                        value={newVIN}
                        onChange={(e) => {
                            const value = e.target.value;
                            setNewVIN(value);
                            if (selectedVIN && isDuplicateVIN(value)) {
                                setNewVINError(t('setup_tool.change_vin.error_vin_exists'));
                            } else {
                                setNewVINError('');
                            }
                        }}
                        variant="outlined"
                        disabled={!selectedVIN}
                        error={!!newVINError}
                        helperText={newVINError || ' '}
                    />
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={isNewInstallation}
                                onChange={(e) => setIsNewInstallation(e.target.checked)}
                                id="new-installation-checkbox"
                                data-testid="new-installation-checkbox"
                            />
                        }
                        label={t('setup_tool.change_vin.new_installation')}
                    />
                </Box>
            </Box>
            <Box sx={{ width: '100%', display: 'flex', p: 2, minHeight: 0, flexShrink: 0, gap: 1 }}>
                <Button id="btn-back" fullWidth variant="contained" color="primary" onClick={() => props.onBack()}>
                    {t('setup_tool.back')}
                </Button>
                <Button
                    id="btn-done"
                    data-testid="btn-done"
                    fullWidth
                    variant="contained"
                    color="secondary"
                    disabled={isActionDisabled || isLoading}
                    onClick={() => handleDone()}
                >
                    {t('setup_tool.completed.done')}
                </Button>
            </Box>
        </>
    );
};

export default ChangeVINSelection;
